import { SPORT_IDS, type SportName } from '@/pages/(guarded)/results/components/consts';
import { request } from '@/renderer/utils/api';
import { IS_SERVER } from '@/renderer/utils/ssr';

export interface DataType {
  //
}

// MLS/CBASE Feb, CBB Nov = 10 months, ~294 days max (3 week buffer) -- this is a magic calc to match seasons together
const SAME_SEASON_MAX_TIME_DIFF = 294 * 24 * 60 * 60 * 1000;

const url = '/api/v1/season?page[limit]=100';

const RASPLUS_SEASONS: API.Season[] = [
  {
    name: 'Fall/Winter 24-25',
    hidden: false,
    id: -1,
    createdAt: '2024',
    endDate: '2025-03-20T06:59:59.000Z',
    startDate: '2024-08-31T07:00:00.000Z',
  },
  {
    name: 'Spring/Summer 24',
    hidden: false,
    id: -2,
    createdAt: '2024',
    endDate: '2024-08-31T06:59:59.000Z',
    startDate: '2024-03-20T07:00:00.000Z',
  },
];

export async function data(pageContext: Vike.PageContext): Promise<DataType> {
  const sportId: string = pageContext.routeParams?.sportId;
  const seasonId: string = pageContext.routeParams?.seasonId;

  try {
    const seasonsRequest = await request<API.WithData<API.Season[]>>(
      {
        input: url,
        init:
          typeof AbortSignal === 'undefined'
            ? {}
            : { signal: AbortSignal.timeout(IS_SERVER ? 750 : 7500) },
      },
      { skipRefresh: true, cache: { maxAge: 10 * 60 * 1000 } },
    );

    const seasonsSimple = getSeasonsSimple(
      sportId === 'plus' ? RASPLUS_SEASONS : seasonsRequest?.data,
      seasonId,
      sportId,
    );

    return { seasons: seasonsRequest?.data, seasonsSimple };
  } catch (error) {
    console.error(error);
  }

  return {};
}

function getSeasonsSimple(seasons?: API.Season[], seasonId?: string, sportId?: string) {
  if (seasons?.length) {
    const sportIdIsNumber = !!sportId && (typeof sportId === 'number' || /^[0-9]+$/.test(sportId));

    const searchSports =
      sportId === '0' || sportId === 'all'
        ? undefined
        : !sportId || sportId === 'ras'
          ? [1, 2, 3]
          : sportIdIsNumber
            ? [parseInt(sportId, 10)]
            : SPORT_IDS[sportId as SportName]
              ? [SPORT_IDS[sportId as SportName]]
              : undefined;

    let first: API.Season | undefined = undefined;
    let last: API.Season | undefined = undefined;
    const last5BySportId: API.Season[][] = [];
    const selected: API.Season[] | undefined = [];

    const seasonIdIsNumber =
      !!seasonId && (typeof seasonId === 'number' || /^[0-9]+$/.test(seasonId));

    for (const season of seasons) {
      if (!searchSports || searchSports.includes(season.sportId as API.Id)) {
        if (seasonId && !selected[0]) {
          if (seasonIdIsNumber && `${season.id}` === `${seasonId}`) {
            selected.push(season);
          }
        }

        // if (
        //   (season.stats?.total || 0) +
        //     (season.stats?.played || 0) +
        //     (season.stats?.uncharged || 0) +
        //     (season.stats?.pending || 0) +
        //     (season.stats?.loss || 0) +
        //     (season.stats?.canceled || 0) +
        //     (season.stats?.buyable || 0) +
        //     (season.stats?.push || 0) +
        //     (season.stats?.win || 0) ===
        //   0
        // ) {
        //   // do not include seasons that have no stats and are not buyable
        //   continue;
        // }

        let skipLast5 = false;

        if (!last5BySportId[season.sportId || '0']) {
          last5BySportId[season.sportId || '0'] = [];
        }

        const last5 = last5BySportId[season.sportId || '0'];

        if (last5.length < 5) {
          // Fill the last 5 seasons array to 5 but keep it sorted with latest date at the beginning of the array
          last5.push(season);
          last5.sort(
            (b, a) =>
              (a.endDate ? Date.parse(a.endDate) : 0) - (b.endDate ? Date.parse(b.endDate) : 0),
          );
          skipLast5 = true;
        }

        if (season.endDate) {
          const date = Date.parse(season.endDate);

          // Inject season into last 5 if it belongs
          if (!skipLast5) {
            for (const idx in last5) {
              const prevSeason = last5[idx];

              if (!prevSeason.endDate || date > Date.parse(prevSeason.endDate)) {
                last5.splice(+idx, 0, season);
                last5.length = 5;
                break;
              }
            }
          }

          if (!last?.endDate || date > Date.parse(last.endDate)) {
            last = season;
          }
        }

        if (
          season.startDate &&
          (!first?.startDate || Date.parse(season.startDate) < Date.parse(first.startDate))
        ) {
          first = season;
        }
      }
    }

    // We have to do this because for multi-sport selections, we need to know the date ranges of each season
    const last5: API.Season[] = last5BySportId.flat();
    let len = last5.length;

    if (len > 5) {
      last5.length = len = 5;
    }

    for (const sportSeasons of last5BySportId) {
      for (const idx in sportSeasons) {
        const endDateA = sportSeasons[idx].endDate && new Date(sportSeasons[idx].endDate);
        const endDateB = last5[idx].endDate && new Date(last5[idx].endDate);
        const startDateA = sportSeasons[idx].startDate && new Date(sportSeasons[idx].startDate);
        const startDateB = last5[idx].startDate && new Date(last5[idx].startDate);
        let endDate = endDateB;
        let startDate = startDateB;

        if (
          endDateA &&
          endDateB &&
          endDateA > endDateB &&
          +endDateA - +endDateB < SAME_SEASON_MAX_TIME_DIFF
        ) {
          // keep previous startDate when using new endDate season
          last5[idx] = { ...sportSeasons[idx], startDate: last5[idx].startDate };
          endDate = endDateA;

          if (last && `${idx}` === '0') {
            // apply new endDate to last season
            last = { ...last, endDate: last5[idx].endDate };
          }
        }

        if (
          startDateA &&
          startDateB &&
          startDateA < startDateB &&
          +startDateB - +startDateA < SAME_SEASON_MAX_TIME_DIFF
        ) {
          // keep previous endDate when using new startDate season
          last5[idx] = { ...sportSeasons[idx], endDate: last5[idx].endDate };
          startDate = startDateA;

          if (last && `${idx}` === '0') {
            // apply new startDate to last season
            last = { ...last, startDate: last5[idx].startDate };
          }

          const nextSeason = last5[+idx + 1];
          const nextSeasonEndDate = nextSeason?.endDate && Date.parse(nextSeason.endDate);
          if (nextSeasonEndDate && nextSeasonEndDate > +startDateA) {
            // Correct last season overlap when dates change
            nextSeason.endDate = last5[idx].startDate;
          }
        }

        if (endDate && startDate) {
          const endFullYear = `${endDate.getFullYear()}`;
          const startFullYear = `${startDate.getFullYear()}`;
          const seasonTextMatch = sportSeasons[idx].name?.match(/^([A-Za-z]+)\/([A-Za-z]+) /);

          if (seasonTextMatch) {
            // Keep Fall/Winter and Spring/Summer to name if it is there per RASPLUS_SEASONS
          } else if (endFullYear !== startFullYear) {
            sportSeasons[idx].name =
              `${startFullYear}-${startFullYear[0] === endFullYear[0] ? endFullYear.slice(2) : endFullYear}`;
          }
        }
      }
    }

    if (seasonIdIsNumber) {
      return { last5, first, last, selected };
    }

    switch (seasonId) {
      case 'last-5':
        return { last5, first, last, selected: last5.slice() };
      case 'last-4':
        return { last5, first, last, selected: last5.slice(0, 4) };
      case 'last-3':
        return { last5, first, last, selected: last5.slice(0, 3) };
      case 'last-2':
        return { last5, first, last, selected: last5.slice(0, 2) };
      case 'last':
        return { last5, first, last, selected: last5.slice(1, 2) };
      case 'current':
        return { last5, first, last, selected: last5.slice(0, 1) };
      case 'all-time':
      default:
        return {
          last5,
          first,
          last,
          selected: first && last ? [last, first] : last || first ? [last || first] : [],
        };
    }
  }
}
